import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FgdsProject } from "@domain/project/fgds-project";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import { AdditionalMonitoringConfigSection } from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { GroupSection } from "@domain/project/product-list/group-section";
import { calculateDiscountedTotal } from "@domain/project/product-list/product-list-discount.util";
import {
  additionalProductsLabel,
  attachmentInstructionsLabel,
  buildFgdsAdditionalCategoryDisplayData,
  buildFgdsPlacedGroupDisplayData,
  buildMonitoringAdditionalCategoryDisplayData,
  buildMonitoringPlacedGroupDisplayData,
  FgdsSectionDisplayData,
  getProductSectionHeadline,
  hasRequiredServices,
  isAttachmentInstructionsDisplayData,
  MonitoringSectionDisplayData,
  ProductListAttachmentInstructionsDisplayData,
  ProductListGroupDisplayData,
  productsOnFloorplanLabel,
  servicesLabel,
  ZREF_EUR_COUNTRY,
} from "@domain/project/product-list/product-list-display-data.util";
import { AdditionalProductConfigSection, ProductListService } from "@domain/project/product-list/product-list.service";
import { ProductSection } from "@domain/project/product-list/product-section";
import { ProjectCostService } from "@domain/project/product-list/project-cost.service";
import { ProjectRelatedService } from "@domain/project/project-related-service";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";
import Excel, { Cell } from "exceljs";
import { ExportProductListRenderer } from "./export-product-list.renderer";
import { createProductListTheme } from "./export-product-list.theme";

export class ExportProductListBuilder {
  private readonly LINE_BREAK = " \r\n";
  private readonly MATERIAL_MARKER_COLUMN = 12;
  private readonly MATERIAL_LINE_MARKER = "MATERIAL_LINE";
  private readonly theme = createProductListTheme();

  constructor(
    private projectCostService: ProjectCostService,
    private productListService: ProductListService,
    private countryCurrencyMapper: CountryCurrencyMapper,
  ) {}

  build(
    worksheet: Excel.Worksheet,
    project: FgdsProject,
    groupSections: GroupSection[],
    monitoringGroupSections: MonitoringGroupSection[],
    additionalSections: AdditionalProductConfigSection[] = [],
    additionalMonitoringSections: AdditionalMonitoringConfigSection[] = [],
    latestProductPositionId?: number,
    showZrefPrices = false,
  ) {
    const fgdsPlacedGroups = buildFgdsPlacedGroupDisplayData(
      groupSections,
      (group) => this.productListService.getAttachmentInstructionsForGroup(group),
      (instructions) => this.projectCostService.calculateTotalAttachmentInstructionsPrice(instructions),
    )
      .map((group) => ({
        ...group,
        sections: group.sections.filter((section) => !isAttachmentInstructionsDisplayData(section)),
      }))
      .filter((group) => group.sections.length > 0);
    const monitoringPlacedGroups = buildMonitoringPlacedGroupDisplayData(monitoringGroupSections);
    const fgdsAdditionalCategories = buildFgdsAdditionalCategoryDisplayData(false, additionalSections, {});
    const monitoringAdditionalCategories = buildMonitoringAdditionalCategoryDisplayData(false, additionalMonitoringSections, {});
    const rootAttachmentInstructions = this.productListService.getRootAttachmentInstructions(groupSections, additionalSections);
    const areCurrenciesMixed = showZrefPrices && this.countryCurrencyMapper.getCurrency(project.country) !== "EUR";
    const exportRenderer = this.createExportRenderer();
    exportRenderer.writeHeader(worksheet);
    exportRenderer.writeSheetFormat(worksheet, this.getCurrencyFormat(project.country, showZrefPrices), "##0%");
    exportRenderer.writeAddress(worksheet, project);
    let rowIndex = 5;

    rowIndex = this.addPlacedProductSections(fgdsPlacedGroups, monitoringPlacedGroups, rowIndex, worksheet, showZrefPrices);
    rowIndex = this.addAdditionalProductSections(
      fgdsAdditionalCategories,
      monitoringAdditionalCategories,
      rowIndex,
      worksheet,
      showZrefPrices,
    );
    if (rootAttachmentInstructions.length > 0) {
      rowIndex = this.addProductHeader(
        attachmentInstructionsLabel,
        worksheet,
        rowIndex,
        this.calculateAttachmentInstructionsPrice(rootAttachmentInstructions, showZrefPrices),
        true,
      );
      rowIndex = this.addProductData(rootAttachmentInstructions, worksheet, rowIndex, 1, 1, true, showZrefPrices);
    }

    if (hasRequiredServices(project)) {
      rowIndex = this.addProductHeader(servicesLabel, worksheet, rowIndex, undefined, true);
    }
    const firstRowOfServices = rowIndex;
    const localCurrencyFormat = areCurrenciesMixed ? this.getCurrencyFormat(project.country) : undefined;
    rowIndex = this.addServices(project, worksheet, rowIndex, latestProductPositionId, localCurrencyFormat);
    const lastRowOfServices = Math.max(rowIndex - 1, firstRowOfServices);
    rowIndex = exportRenderer.writeTotals(worksheet, rowIndex, firstRowOfServices, lastRowOfServices, areCurrenciesMixed);
    exportRenderer.writeChecklist(worksheet, rowIndex);
  }

  private addFgdsPlacedGroup(
    group: ProductListGroupDisplayData<FgdsSectionDisplayData, ProductListAttachmentInstructionsDisplayData, GroupSection>,
    rowIndex: number,
    worksheet: Excel.Worksheet,
    showZrefPrices: boolean,
  ): number {
    group.sections.forEach((section) => {
      if (isAttachmentInstructionsDisplayData(section)) {
        const productSectionHeader = `${group.label} - ${section.label}`;
        rowIndex = this.addProductHeader(productSectionHeader, worksheet, rowIndex);
        rowIndex = this.addProductData(section.attachmentInstructions, worksheet, rowIndex, 1, 1, true, showZrefPrices);
        return;
      }

      rowIndex = this.addProductSection(section.section, group.label, rowIndex, worksheet, showZrefPrices);
    });

    return rowIndex;
  }

  private addPlacedProductSections(
    fgdsPlacedGroups: ProductListGroupDisplayData<
      FgdsSectionDisplayData,
      ProductListAttachmentInstructionsDisplayData,
      GroupSection
    >[],
    monitoringPlacedGroups: ProductListGroupDisplayData<MonitoringSectionDisplayData, never, MonitoringGroupSection>[],
    rowIndex: number,
    worksheet: Excel.Worksheet,
    showZrefPrices: boolean,
  ) {
    if (fgdsPlacedGroups.length === 0 && monitoringPlacedGroups.length === 0) {
      return rowIndex;
    }

    const placedProductsSubtotal = showZrefPrices
      ? fgdsPlacedGroups.reduce((total, group) => total + (group.source.zrefSubTotal ?? 0), 0)
      : fgdsPlacedGroups.reduce((total, group) => total + (group.subtotal ?? 0), 0);
    rowIndex = this.addProductHeader(
      productsOnFloorplanLabel,
      worksheet,
      rowIndex,
      fgdsPlacedGroups.length > 0 ? placedProductsSubtotal : undefined,
      true,
    );

    fgdsPlacedGroups.forEach((group) => {
      rowIndex = this.addFgdsPlacedGroup(group, rowIndex, worksheet, showZrefPrices);
    });

    return this.addMonitoringPlacedGroups(monitoringPlacedGroups, rowIndex, worksheet);
  }

  private addAdditionalProductSections(
    fgdsAdditionalCategories: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>,
    monitoringAdditionalCategories: ReturnType<typeof buildMonitoringAdditionalCategoryDisplayData>,
    rowIndex: number,
    worksheet: Excel.Worksheet,
    showZrefPrices: boolean,
  ): number {
    if (fgdsAdditionalCategories.length === 0 && monitoringAdditionalCategories.length === 0) {
      return rowIndex;
    }

    const additionalProductsSubtotal = showZrefPrices
      ? fgdsAdditionalCategories.reduce((total, category) => total + (category.zrefSubTotal ?? 0), 0)
      : fgdsAdditionalCategories.reduce((total, category) => total + (category.subtotal ?? 0), 0);
    rowIndex = this.addProductHeader(
      additionalProductsLabel,
      worksheet,
      rowIndex,
      fgdsAdditionalCategories.length > 0 ? additionalProductsSubtotal : undefined,
      true,
    );

    fgdsAdditionalCategories.forEach((category) => {
      rowIndex = this.addFgdsAdditionalCategory(category, worksheet, rowIndex, showZrefPrices);
    });
    monitoringAdditionalCategories.forEach((category) => {
      rowIndex = this.addMonitoringAdditionalCategory(category, worksheet, rowIndex);
    });
    return rowIndex;
  }

  private addFgdsAdditionalCategory(
    category: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>[number],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    showZrefPrices: boolean,
  ): number {
    rowIndex = this.addProductHeader(category.label, worksheet, rowIndex);
    category.configs.forEach((config) => {
      rowIndex = this.addAdditionalFgdsConfig(config, worksheet, rowIndex, showZrefPrices);
      rowIndex = this.addProductData(
        config.subproducts,
        worksheet,
        rowIndex,
        config.savedQuantity,
        undefined,
        false,
        showZrefPrices,
      );
    });
    return rowIndex;
  }

  private addAdditionalFgdsConfig(
    config: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>[number]["configs"][number],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    showZrefPrices = false,
  ) {
    const mainProduct = config.mainProduct;
    const unitPrice = mainProduct
      ? showZrefPrices
        ? mainProduct.zrefPriceData?.price
        : mainProduct.priceData?.price
      : showZrefPrices
        ? (config.zrefUnitPrice ?? undefined)
        : (config.unitPrice ?? 0);
    const discount = mainProduct?.discount ?? 0;
    worksheet.getRow(rowIndex).values = [
      config.positionNumber,
      config.mainProductId ?? "--",
      config.savedQuantity,
      config.name,
      $localize`:@@productListExport.productUnit.abbreviation:St`,
      unitPrice,
      discount / 100,
    ];
    this.applyBodyRowStyle(worksheet, rowIndex);
    this.applyProductRowFormulas(worksheet, rowIndex);
    this.applyMaterialLineMarker(worksheet, rowIndex);
    return rowIndex + 1;
  }

  private addMonitoringPlacedGroups(
    groups: ProductListGroupDisplayData<MonitoringSectionDisplayData, never, MonitoringGroupSection>[],
    rowIndex: number,
    worksheet: Excel.Worksheet,
  ): number {
    groups.forEach((group) => {
      group.sections.forEach((section) => {
        const productSectionHeader = `${group.label} - ${section.label}`;
        rowIndex = this.addProductHeader(productSectionHeader, worksheet, rowIndex);
        rowIndex = this.addMonitoringProductSection(section.section, worksheet, rowIndex);
      });
    });

    return rowIndex;
  }

  private addMonitoringAdditionalCategory(
    category: ReturnType<typeof buildMonitoringAdditionalCategoryDisplayData>[number],
    worksheet: Excel.Worksheet,
    rowIndex: number,
  ): number {
    rowIndex = this.addProductHeader(category.label, worksheet, rowIndex);
    category.configs.forEach((config) => {
      rowIndex = this.writeMonitoringRow(
        worksheet,
        rowIndex,
        config.positionNumber,
        config.mainProductId ?? "--",
        config.savedQuantity,
        config.name,
      );
      rowIndex = this.addMonitoringProductData(config.subproducts, worksheet, rowIndex, config.savedQuantity);
    });
    return rowIndex;
  }

  private addMonitoringProductSection(
    section: MonitoringSectionDisplayData,
    worksheet: Excel.Worksheet,
    rowIndex: number,
  ): number {
    section.mappings.forEach((mapping) => {
      rowIndex = this.addMonitoringProductData(mapping.data, worksheet, rowIndex, mapping.placedProductsCount);

      section.devices.forEach((device) => {
        if (device.products) {
          device.products.forEach((product) => {
            if (product.id && this.monitoringProductBelongsToConfig(device, mapping)) {
              rowIndex = this.addMonitoringPlaceholderProducts([product], worksheet, rowIndex, 1);
            }
          });
        }
      });
    });

    section.placeholders.forEach((placeholder) => {
      const placeholderProducts = placeholder.products.filter((product) => product.id);
      if (placeholderProducts.length > 0) {
        rowIndex = this.addMonitoringPlaceholderProducts(placeholderProducts, worksheet, rowIndex, 1);
      }
    });

    const unspecifiedPlaceholderCount = this.productListService.countUnspecifiedPlaceholders(section.placeholders);
    if (unspecifiedPlaceholderCount > 0) {
      rowIndex = this.addMonitoringPlaceholderRow(
        section.placeholderPositionPrefix,
        unspecifiedPlaceholderCount,
        worksheet,
        rowIndex,
      );
    }

    return rowIndex;
  }

  private addMonitoringProductData(
    productData: MonitoringProductData[],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    placedProductCount: number,
  ): number {
    productData.forEach((data) => {
      const quantity = data.quantity * placedProductCount;
      rowIndex = this.writeMonitoringRow(worksheet, rowIndex, data.positionId, data.matNr, quantity, data.name);
    });

    return rowIndex;
  }

  private addMonitoringPlaceholderProducts(
    products: PlaceholderProduct[],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    quantity: number,
  ): number {
    products.forEach((product) => {
      rowIndex = this.writeMonitoringRow(worksheet, rowIndex, product.positionId, product.id, quantity, product.name);
    });

    return rowIndex;
  }

  private addMonitoringPlaceholderRow(
    positionPrefix: string,
    count: number,
    worksheet: Excel.Worksheet,
    rowIndex: number,
  ): number {
    worksheet.getRow(rowIndex).values = [
      positionPrefix,
      "",
      count,
      $localize`:@@productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
      "",
      "",
      "",
      "",
      "",
    ];
    this.applyBodyRowStyle(worksheet, rowIndex);
    rowIndex += 1;
    return rowIndex;
  }

  private addProductSection(
    productSection: ProductSection<
      | FloorplanAlarmDevice
      | FloorplanGasWarningCenter
      | FloorplanPlasticSign
      | FloorplanSignalElement
      | FloorplanTransmitter
      | FloorplanFlameDetector,
      FloorplanPlaceholder,
      | AlarmDeviceConfiguration
      | GasWarningCenterConfiguration
      | PlasticSignConfiguration
      | SignalElementConfiguration
      | TransmitterConfiguration
      | FlameDetectorConfiguration,
      ConfigProductMap
    >,
    groupName: string,
    rowIndex: number,
    worksheet: Excel.Worksheet,
    showZrefPrices = false,
  ): number {
    const placeholderProducts = this.productListService.getProductsWithIdFromPlaceholders(productSection.placeholders);

    if (productSection.mappings.length > 0 || placeholderProducts.length > 0) {
      const productSectionHeader = `${groupName} - ${getProductSectionHeadline(productSection.productType)}`;
      rowIndex = this.addProductHeader(productSectionHeader, worksheet, rowIndex);
      productSection.mappings.forEach((mapping: ConfigProductMap) => {
        rowIndex = this.addProductData(
          mapping.data,
          worksheet,
          rowIndex,
          mapping.placedProductsCount,
          undefined,
          true,
          showZrefPrices,
        );

        productSection.devices.forEach((device) => {
          if (device.products) {
            device.products.forEach((product) => {
              if (product.id && this.productBelongsToConfig(device, mapping)) {
                if (!product.name) product.name = $localize`:@@customPartNumber.placeholderName:Benutzerdefinierte Sachnummer`;
                rowIndex = this.addProductData([product], worksheet, rowIndex, 1, undefined, true, showZrefPrices);
              }
            });
          }
        });
      });

      if (placeholderProducts.length > 0) {
        rowIndex = this.addProductData(placeholderProducts, worksheet, rowIndex, 1, undefined, true, showZrefPrices);
      }
    }
    const unspecifiedPlaceholderCount = this.productListService.countUnspecifiedPlaceholders(productSection.placeholders);
    if (unspecifiedPlaceholderCount > 0) {
      const placeholderSectionHeader = `${groupName} - ${getProductSectionHeadline(productSection.productType, true)}`;
      rowIndex = this.addProductHeader(placeholderSectionHeader, worksheet, rowIndex);
      const unspecifiedPlaceholder = new PlaceholderProduct(
        $localize`:@@productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
        "",
        productSection.placeholderPositionPrefix,
      );
      rowIndex = this.addProductData([unspecifiedPlaceholder], worksheet, rowIndex, unspecifiedPlaceholderCount);
    }
    return rowIndex;
  }

  private getCurrencyFormat(country: string, showZrefPrices = false) {
    const effectiveCountry = showZrefPrices ? ZREF_EUR_COUNTRY : country;
    return `#,##0.00 [$${this.setCurrencySymbol(effectiveCountry)}-${this.countryCurrencyMapper.getExcelLocale(effectiveCountry)}]`;
  }

  private addProductData(
    productData: ProductData[] | PlaceholderProduct[],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    placedProductCount: number,
    maxQuantity?: number,
    includeInMaterialTotal = true,
    showZrefPrices = false,
  ): number {
    productData.forEach((data: ProductData | PlaceholderProduct) => {
      const quantity = maxQuantity ?? (data instanceof ProductData ? data.quantity * placedProductCount : placedProductCount);
      const price =
        "priceData" in data
          ? showZrefPrices
            ? data.zrefPriceData?.price
            : data.priceData?.price
          : showZrefPrices
            ? undefined
            : (data as PlaceholderProduct).price?.price;
      const discount = data.discount ?? 0;
      worksheet.getRow(rowIndex).values = [
        data.positionId,
        data.id,
        quantity,
        data.name,
        $localize`:@@productListExport.productUnit.abbreviation:St`,
        price,
        discount / 100,
      ];
      this.applyBodyRowStyle(worksheet, rowIndex);
      this.applyProductRowFormulas(worksheet, rowIndex);
      if (includeInMaterialTotal) {
        this.applyMaterialLineMarker(worksheet, rowIndex);
      }
      rowIndex += 1;
    });
    return rowIndex;
  }

  private addServices(
    project: FgdsProject,
    worksheet: Excel.Worksheet,
    row: number,
    latestProductPositionId?: number,
    localCurrencyFormat?: string,
  ): number {
    let nextServicePosition = latestProductPositionId ?? 0;
    const services = [
      {
        name: $localize`:@@global.assembly:Montage`,
        position: this.projectCostService.getAssemblyPosition(project).toString(),
        service: project.assembly,
        id: "1965124",
      },
      {
        name: $localize`:@@global.installation:Inbetriebnahme`,
        position: this.projectCostService.getInstallationPosition(project).toString(),
        service: project.installation,
        id: "1947222",
      },
      {
        name: $localize`:@@global.documentation:Dokumentation`,
        position: this.projectCostService.getDocumentationPosition(project).toString(),
        service: project.documentation,
        id: "3721413",
      },
      {
        name: $localize`:@@global.engineering:Engineering`,
        position: this.projectCostService.getEngineeringPosition(project).toString(),
        service: project.engineering,
        id: "1907670",
      },
      {
        name: $localize`:@@global.additionalServices:Sonstige Dienstleistungen`,
        position: this.projectCostService.getAdditionalServicesPosition(project).toString(),
        service: project.additionalServices,
        id: "",
      },
    ];
    services.forEach((entry) => {
      const position =
        latestProductPositionId === undefined ? entry.position : this.getNextServicePosition(entry.service, nextServicePosition);
      nextServicePosition = Number(position);
      row = this.addService(entry.name, position, entry.service, entry.id, worksheet, row, localCurrencyFormat);
    });
    return row;
  }

  private getNextServicePosition(service: ProjectRelatedService, currentPosition: number): string {
    return service.required ? (currentPosition + 1).toString() : currentPosition.toString();
  }

  private addService(
    name: string,
    position: string,
    service: ProjectRelatedService,
    id: string,
    worksheet: Excel.Worksheet,
    row: number,
    localCurrencyFormat?: string,
  ): number {
    if (service.required) {
      worksheet.getCell(row, 4).value = name;
      this.getCells(row, row, 1, 9, worksheet).forEach((cell) => (cell.fill = this.theme.fills.header));
      worksheet.getRow(row++).font = this.theme.fonts.bold;
      worksheet.getRow(row).values = [
        position,
        id,
        "1",
        service.notes,
        $localize`:@@productListExport.serviceUnit.abbreviation:D-St`,
        "",
        "",
        "",
        service.costs!,
      ];
      if (localCurrencyFormat) {
        worksheet.getCell(row, 9).numFmt = localCurrencyFormat;
      }
      worksheet.getRow(row++).font = this.theme.fonts.normal;
    }
    return row;
  }

  private writeMonitoringRow(
    worksheet: Excel.Worksheet,
    rowIndex: number,
    positionId: string | undefined,
    matNr: string,
    quantity: number,
    name: string,
  ) {
    worksheet.getRow(rowIndex).values = [
      positionId,
      matNr,
      quantity,
      name,
      $localize`:@@productListExport.productUnit.abbreviation:St`,
      undefined,
      undefined,
      undefined,
      undefined,
    ];
    this.applyBodyRowStyle(worksheet, rowIndex);
    return rowIndex + 1;
  }

  private applyBodyRowStyle(worksheet: Excel.Worksheet, rowIndex: number) {
    worksheet.getCell(rowIndex, 4).alignment = { wrapText: true };
    worksheet.getRow(rowIndex).font = this.theme.fonts.normal;
  }

  private applyProductRowFormulas(worksheet: Excel.Worksheet, rowIndex: number) {
    worksheet.getCell(rowIndex, 8).value = {
      formula: `(F${rowIndex}*(1-G${rowIndex}))`,
    };
    worksheet.getCell(rowIndex, 9).value = {
      formula: `(C${rowIndex}*H${rowIndex})`,
    };
  }

  private applyMaterialLineMarker(worksheet: Excel.Worksheet, rowIndex: number) {
    worksheet.getCell(rowIndex, this.MATERIAL_MARKER_COLUMN).value = this.MATERIAL_LINE_MARKER;
  }

  private calculateAttachmentInstructionsPrice(instructions: ProductData[], showZrefPrices: boolean): number {
    this.projectCostService.calculateTotalAttachmentInstructionsPrice(instructions, showZrefPrices);
    return instructions.reduce((sum, instruction) => {
      const price = (showZrefPrices ? instruction.zrefPriceData?.price : instruction.priceData?.price) ?? 0;
      return sum + calculateDiscountedTotal(price, instruction.quantity, instruction.discount ?? 0);
    }, 0);
  }

  private getCells(fromRow: number, toRow: number, fromCol: number, toCol: number, worksheet: Excel.Worksheet): Cell[] {
    const cells = [];
    for (let col = fromCol; col <= toCol; col++) {
      for (let row = fromRow; row <= toRow; row++) {
        cells.push(worksheet.getCell(row, col));
      }
    }
    return cells;
  }

  private setCurrencySymbol(country: string) {
    const currencyCode = this.countryCurrencyMapper.getCurrency(country);

    const formatted = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "symbol",
    }).format(1);
    return formatted.replace(/[\d\s.,]/g, "");
  }

  private addProductHeader(
    header: string,
    worksheet: Excel.Worksheet,
    rowIndex: number,
    subtotal?: number,
    isRootSection = false,
  ) {
    this.setSubHeaderStyle(worksheet, rowIndex, isRootSection);
    worksheet.getCell(rowIndex, 4).value = header;
    if (subtotal !== undefined) {
      worksheet.getCell(rowIndex, 9).value = subtotal;
    }
    return rowIndex + 1;
  }

  private setSubHeaderStyle(worksheet: Excel.Worksheet, rowIndex: number, isRootSection = false) {
    worksheet.getRow(rowIndex).font = isRootSection ? this.theme.fonts.rootBold : this.theme.fonts.bold;
    this.getCells(rowIndex, rowIndex, 1, 9, worksheet).forEach((cell) => (cell.fill = this.theme.fills.header));
  }

  private productBelongsToConfig(
    product:
      | FloorplanGasWarningCenter
      | FloorplanAlarmDevice
      | FloorplanSignalElement
      | FloorplanPlasticSign
      | FloorplanTransmitter
      | FloorplanFlameDetector,
    mapping: ConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private monitoringProductBelongsToConfig(
    product:
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
    mapping: MonitoringConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private createExportRenderer() {
    return new ExportProductListRenderer(this.theme, this.LINE_BREAK);
  }
}
